import { APPLICATION_IMPORTANCE_LIST } from '../../../../ng/components/jenkins-controller/legacy-constants'
import { JiraIntegrationForm } from '../jira-integration-page/jira-form-schema'
import { isEmpty } from '../../../../util'
import {
  ApiJiraIntegrationForm,
  Application,
  BugtrackerConfig,
  FormProjectField,
  Importance,
  IssueType,
  Project,
  ProjectFieldTypes,
  ProjectUser,
  ServerlessApiAccount,
  IntegrationConfig,
  Rule,
  NgBugtrackerMaintenanceWindowResource
} from './shared'
import { DEFAULT_AUTO_CREATE_TICKETS_SEVERITIES } from '../jira-integration-page/jira-form-field-types'

export const mapApiBugtrackerToUI = (
  bugtracker: BugtrackerConfig,
  integrations?: IntegrationConfig[],
  maintenanceWindow?: NgBugtrackerMaintenanceWindowResource
): JiraIntegrationForm | {} => {
  if (bugtracker) {
    const {
      credentials,
      // remove these API returned props since the UI does not use them
      username: _username,
      host: _host,
      links: _links,
      bugtracker_id: _bugtrackerId,
      exclude_sensitive_info: _excludeSensitiveInfo,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      send_tags: _send_tags,
      auto_create_tickets_all_rules: _autoCreateTicketsAllRules,
      ...bugTrackerAPIProps
    } = bugtracker
    // remove id from credentials
    const { id: credentialsId, ...uiCredentialsProps } = credentials
    return {
      ...bugTrackerAPIProps,
      credentials: { ...uiCredentialsProps },
      exclude_sensitive_info: _excludeSensitiveInfo,
      send_tags: _send_tags,
      credentials_id: credentialsId,
      importance: transformApiImportance(bugtracker.importance),
      auto_create_tickets_severities: transformSeverities(bugtracker),
      integrations,
      maintenance_windows: maintenanceWindow,
      ...(integrations && integrations.length ? { integration: true } : {})
    }
  }
  return {}
}

export const transformSeverities = (
  bugtracker: Pick<
    BugtrackerConfig,
    'auto_create_tickets_all_rules' | 'auto_create_tickets_severities'
  >
) => {
  return !bugtracker.auto_create_tickets_all_rules &&
    isEmpty(bugtracker.auto_create_tickets_severities)
    ? DEFAULT_AUTO_CREATE_TICKETS_SEVERITIES
    : bugtracker.auto_create_tickets_severities
}

const transformApiImportance = (importances: string[]) =>
  importances
    .map((importance) =>
      APPLICATION_IMPORTANCE_LIST.find((item) => item.value === importance)
    )
    .filter((importance) => importance) as Importance[]

export const mapJiraFormToApi = ({
  credentials,
  applications,
  importance,
  auto_create_tickets_enabled,
  auto_create_tickets_rules,
  auto_create_tickets_severities,
  project,
  assignee,
  issue_type,
  fields,
  serverless_account_ids,
  serverless,
  integrations,
  maintenance_windows,
  priority_field_available,
  integration: _integration,
  epic_id,
  exclude_sensitive_info,
  ...matchingProperties
}: JiraIntegrationForm): ApiJiraIntegrationForm => ({
  ...matchingProperties,
  credentials: transformCredentials(credentials),
  applications: matchingProperties.app_criterion
    ? transformApplications(applications)
    : [],
  all_applications:
    matchingProperties.app_criterion && applications.length === 0,
  importance: matchingProperties.app_criterion
    ? []
    : transformImportance(importance as Importance[]),
  auto_create_tickets_enabled: auto_create_tickets_enabled || serverless,
  auto_create_tickets_rules: transformAutoTicketsRules(
    auto_create_tickets_rules
  ),
  priority_field_available: !!priority_field_available,
  auto_create_tickets_severities,
  auto_create_tickets_all_rules:
    isEmpty(auto_create_tickets_rules) &&
    isEmpty(auto_create_tickets_severities),
  project: transformProject(project),
  assignee: transformAssignee(assignee),
  issue_type: transformIssueType(issue_type),
  fields: transformField(fields),
  serverless_account_ids: transformServerlessAccounts(serverless_account_ids),
  serverless,
  integrations: transformIntegrations(integrations),
  maintenance_windows,
  epic_id: transformEpic(epic_id),
  exclude_sensitive_info
})

/** this is needed to enforce type safety */
const transformCredentials = (credentials) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { id, ...apiCredentials } = credentials ?? { id: undefined }

  return credentials
    ? {
        ...apiCredentials,
        credentials_update: !!credentials.credentials_update
      }
    : undefined
}

const transformEpic = (epic) => {
  return epic?.id ? epic.id : undefined
}

export const transformField = (fields: FormProjectField[]) =>
  fields.map(({ id: _id, ...field }) => {
    if (field._projectField?.schema.type === ProjectFieldTypes.USER) {
      return {
        ...field,
        value: typeof field.value === 'string' ? field.value : field.value?.key,
        _projectField: undefined
      }
    }
    return { ...field, _projectField: undefined }
  })
export const transformProject = (project: Project | string) =>
  typeof project === 'string' ? project : project.id
export const transformAssignee = (assignee: ProjectUser | string) =>
  typeof assignee === 'string' ? assignee : assignee.accountId
export const transformIssueType = (issueType: IssueType | string) =>
  typeof issueType === 'string' ? parseInt(issueType, 10) : issueType.id
const transformImportance = (importance: Importance[]) =>
  importance.map((item) => item.value)

type ReplaceReturnType<T extends (...a: any) => any, TNewReturn> = (
  ...a: Parameters<T>
) => TNewReturn

/**
 * Map array of T to array of strings or leave as is if already an array of strings.
 * This is required since applications/accounts will not be converted from strings if their component is not loaded.
 * @param arr
 * @param callbackfn map callback function
 * @returns
 */
export const mapToStringArray = <T>(
  arr: string[] | T[],
  // Get standard Array.map callback function type.
  // Not sure how to pass type generic to map in this case so settings return value manually.
  callbackfn: ReplaceReturnType<Parameters<T[]['map']>[0], string>
): string[] => {
  // Array is empty, may be considered as strings
  if (arr?.length < 1) {
    return arr as string[]
  }
  // Array is already in target format, leave as is
  if (typeof arr[0] === 'string') {
    return arr as string[]
  }

  const knownArray = arr as T[]
  return knownArray.map(callbackfn)
}
const transformApplications = (applications: string[] | Application[]) =>
  mapToStringArray(applications, (application) => application.appId)
const transformServerlessAccounts = (
  accounts: string[] | ServerlessApiAccount[]
) => mapToStringArray(accounts, (account) => account.accountId)

export const transformIntegrations = (
  integrations?: IntegrationConfig[]
): ApiJiraIntegrationForm['integrations'] => {
  return Array.isArray(integrations)
    ? integrations.map(
        ({
          issue_resolution,
          issue_status,
          trace_status,
          trace_substatus
        }) => ({
          ...(issue_resolution && typeof issue_resolution !== 'string'
            ? { issueResolution: issue_resolution.id }
            : {}),
          issueStatus:
            typeof issue_status !== 'string' ? issue_status?.id : issue_status,
          traceStatus:
            typeof trace_status !== 'string'
              ? trace_status?.value
              : trace_status,
          ...(trace_substatus && typeof trace_substatus !== 'string'
            ? { traceSubstatus: trace_substatus.value }
            : {})
        })
      )
    : []
}

export const transformAutoTicketsRules = (rules: Rule[]) =>
  mapToStringArray(rules, (rule) => rule.name)
